import { EyeIcon, HeartIcon, MessagesSquareIcon } from 'lucide-react'
import type React from 'react'

import {
  AnimatedTooltip,
  IdeaCard,
  IdeaCardBadge,
  IdeaCardChallenge,
  IdeaCardContent,
  IdeaCardDescription,
  IdeaCardHeader,
  IdeaCardParticipants,
  IdeaCardSequence,
  IdeaCardSimilarity,
  IdeaCardStatus,
  IdeaCardTitle,
  Separator,
} from '@/components/ui'
import { cn } from '@/lib/cn'
import { sanitizeHTML } from '@/utils/sanitize'

export interface ISimilarityIdeaCardProps {
  id: string
  sequence: string
  title: string
  description: string
  status: { name: string; color: string }
  views: number
  likes: number
  comments: number
  participants: { name: string; image?: string }[]
  challenge: { title: string; sequence: string }
  similarity: number
  onClickCard: (id: string) => void
}

const SimilarityIdeaCard: React.FC<ISimilarityIdeaCardProps> = ({
  id,
  sequence,
  title,
  description,
  status,
  views,
  likes,
  comments,
  participants,
  challenge,
  similarity,
  onClickCard,
}) => {
  return (
    <IdeaCard
      onClick={() => {
        onClickCard(id)
      }}
    >
      <IdeaCardHeader>
        <IdeaCardSequence>#{sequence.padStart(6, '0')}</IdeaCardSequence>
        <IdeaCardTitle>{title}</IdeaCardTitle>
      </IdeaCardHeader>

      <IdeaCardContent>
        <IdeaCardDescription
          dangerouslySetInnerHTML={{
            __html: sanitizeHTML(description),
          }}
        />

        <div className="flex items-center gap-4">
          <IdeaCardBadge>
            <EyeIcon />
            {views}
          </IdeaCardBadge>

          <IdeaCardBadge>
            <HeartIcon className={cn('text-system-primary-icon')} />
            {likes}
          </IdeaCardBadge>

          <IdeaCardBadge>
            <MessagesSquareIcon />
            {comments}
          </IdeaCardBadge>
        </div>

        <Separator />

        <div className="flex gap-6">
          <IdeaCardParticipants>
            <div className="flex w-full flex-row items-center">
              <AnimatedTooltip
                items={participants.map((participant) => ({
                  id: participant.name,
                  designation: '',
                  name: participant.name,
                  image: participant.image ?? '',
                }))}
              />
            </div>
          </IdeaCardParticipants>

          <IdeaCardStatus color={status.color}>{status.name}</IdeaCardStatus>

          <IdeaCardSimilarity className="bg-gradient-to-r from-[#A0468A] to-[#6B4CD6] bg-clip-text font-semibold text-transparent">
            {similarity}%
          </IdeaCardSimilarity>
        </div>
      </IdeaCardContent>

      <IdeaCardChallenge
        title={challenge.title}
        sequence={challenge.sequence}
      />
    </IdeaCard>
  )
}

SimilarityIdeaCard.displayName = 'SimilarityIdeaCard'

export { SimilarityIdeaCard }
